import * as i0 from '@angular/core';
import { Directive, Injectable, PLATFORM_ID, inject, DestroyRef, ApplicationRef, ChangeDetectorRef, Component, ChangeDetectionStrategy, Input, ViewChild, HostListener } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject, debounceTime, first, interval, switchMap, filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { iconArrowLeft, iconArrowRight, IconComponent } from '@knorr-bremse-portals/ngx-components/components/icon';

class GalleryPageDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: GalleryPageDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.7", type: GalleryPageDirective, isStandalone: true, selector: "[kbGalleryPage]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: GalleryPageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[kbGalleryPage]',
                    standalone: true,
                }]
        }] });

function provideGalleryIntl(intl) {
    return {
        provide: GalleryIntl,
        useClass: intl,
    };
}
class GalleryIntl {
    constructor() {
        this.changes$ = new Subject();
        this.previousPageButtonLabel = 'Previous page';
        this.nextPageButtonLabel = 'Next page';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: GalleryIntl, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: GalleryIntl, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: GalleryIntl, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class GalleryComponent {
    static { this.pageSwitchTime = 5000; } // TODO: move the time to a central place
    constructor() {
        this.iconArrowLeft = iconArrowLeft;
        this.iconArrowRight = iconArrowRight;
        this.automaticNavigation = false;
        this.resize$ = new Subject();
        this.hasPages = false;
        this.isPlatformBrowser = isPlatformBrowser(inject(PLATFORM_ID));
        this.destroyRef = inject(DestroyRef);
        this.appRef = inject(ApplicationRef);
        this.cdr = inject(ChangeDetectorRef);
        this.intl = inject(GalleryIntl);
        this.hover = false;
        this.intl.changes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.markForCheck());
    }
    ngOnInit() {
        if (this.automaticNavigation) {
            this.initAutomaticNavigation();
        }
        if (this.isPlatformBrowser) {
            this.resizeObserver = new ResizeObserver(() => {
                this.resize$.next();
            });
            this.resizeObserver.observe(this.pagesElement.nativeElement);
            this.resize$.pipe(debounceTime(100)).subscribe(() => {
                const pagesContainer = this.pagesElement.nativeElement;
                const scrollViewportWidth = pagesContainer.offsetWidth;
                const scrollWidth = pagesContainer.scrollWidth;
                const pages = (scrollWidth / scrollViewportWidth) - 1;
                const hasPages = pages > 0;
                if (hasPages !== this.hasPages) {
                    this.hasPages = hasPages;
                    this.cdr.markForCheck();
                }
            });
        }
    }
    ngOnDestroy() {
        this.resize$.complete();
        this.resizeObserver?.disconnect();
    }
    enableHover() {
        this.hover = true;
    }
    disableHover() {
        this.hover = false;
    }
    initAutomaticNavigation() {
        if (!this.isPlatformBrowser) {
            return;
        }
        this.appRef.isStable.pipe(first(stable => stable), switchMap(() => interval(GalleryComponent.pageSwitchTime)), filter(() => !this.hover), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.openNextPage();
        });
    }
    navigatePage(next) {
        const pagesContainer = this.pagesElement.nativeElement;
        const scrollViewportWidth = pagesContainer.offsetWidth;
        const scrollWidth = pagesContainer.scrollWidth;
        const scrollLeft = Math.ceil(pagesContainer.scrollLeft); // This is because this returns strange float numbers
        const pages = (scrollWidth / scrollViewportWidth) - 1;
        const activePage = scrollLeft / scrollViewportWidth;
        let targetPage = activePage + (next ? 1 : -1);
        if (targetPage > pages && scrollLeft >= (scrollWidth - scrollViewportWidth)) {
            targetPage = 0;
        }
        else if (targetPage < 0 && activePage <= 0) {
            targetPage = pages;
        }
        const targetScrollLeft = Math.ceil(targetPage * scrollViewportWidth);
        pagesContainer.scrollTo({
            left: targetScrollLeft,
            behavior: 'smooth',
        });
    }
    openPreviousPage() {
        this.navigatePage(false);
    }
    openNextPage() {
        this.navigatePage(true);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: GalleryComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: GalleryComponent, isStandalone: true, selector: "kb-gallery", inputs: { automaticNavigation: "automaticNavigation" }, host: { listeners: { "mouseenter": "enableHover()", "mouseleave": "disableHover()" } }, viewQueries: [{ propertyName: "pagesElement", first: true, predicate: ["pagesElement"], descendants: true, static: true }], ngImport: i0, template: "\n<div class=\"pages\" #pagesElement>\n  <ng-content></ng-content>\n</div>\n<button type=\"button\" [class.hidden]=\"!hasPages\" class=\"page-button previous-page-button\" kb-icon [icon]=\"iconArrowLeft\" [attr.aria-label]=\"intl.previousPageButtonLabel\" (click)=\"openPreviousPage()\"></button>\n<button type=\"button\" [class.hidden]=\"!hasPages\" class=\"page-button next-page-button\" kb-icon [icon]=\"iconArrowRight\" [attr.aria-label]=\"intl.nextPageButtonLabel\" (click)=\"openNextPage()\"></button>\n", styles: [":host{display:block;position:relative;overflow:auto}:host .page-button{position:absolute;top:50%;transform:translateY(-50%);color:var(--kb-color--background);transition:color var(--kb-speed--slow),opacity var(--kb-speed--slow);cursor:pointer;filter:drop-shadow(0 0 5px #000);font-size:calc(var(--kb-typography--size-extra-large) * 3)}:host .page-button:focus{outline:none}:host .page-button:hover,:host .page-button:focus{color:var(--kb-color--primary)}:host .page-button.hidden{display:none}:host .previous-page-button{left:0}:host .next-page-button{right:0}:host .pages{display:flex;scroll-snap-type:x mandatory;overflow-x:scroll;scroll-behavior:smooth;scrollbar-width:none}:host .pages::-webkit-scrollbar{display:none}:host .pages ::ng-deep [kbGalleryPage]{flex-basis:100%;flex-grow:0;flex-shrink:0;scroll-snap-align:start;scroll-snap-stop:always}\n"], dependencies: [{ kind: "component", type: IconComponent, selector: "kb-icon, button[kb-icon], a[kb-icon]", inputs: ["icon"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: GalleryComponent, decorators: [{
            type: Component,
            args: [{ selector: 'kb-gallery', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [IconComponent], template: "\n<div class=\"pages\" #pagesElement>\n  <ng-content></ng-content>\n</div>\n<button type=\"button\" [class.hidden]=\"!hasPages\" class=\"page-button previous-page-button\" kb-icon [icon]=\"iconArrowLeft\" [attr.aria-label]=\"intl.previousPageButtonLabel\" (click)=\"openPreviousPage()\"></button>\n<button type=\"button\" [class.hidden]=\"!hasPages\" class=\"page-button next-page-button\" kb-icon [icon]=\"iconArrowRight\" [attr.aria-label]=\"intl.nextPageButtonLabel\" (click)=\"openNextPage()\"></button>\n", styles: [":host{display:block;position:relative;overflow:auto}:host .page-button{position:absolute;top:50%;transform:translateY(-50%);color:var(--kb-color--background);transition:color var(--kb-speed--slow),opacity var(--kb-speed--slow);cursor:pointer;filter:drop-shadow(0 0 5px #000);font-size:calc(var(--kb-typography--size-extra-large) * 3)}:host .page-button:focus{outline:none}:host .page-button:hover,:host .page-button:focus{color:var(--kb-color--primary)}:host .page-button.hidden{display:none}:host .previous-page-button{left:0}:host .next-page-button{right:0}:host .pages{display:flex;scroll-snap-type:x mandatory;overflow-x:scroll;scroll-behavior:smooth;scrollbar-width:none}:host .pages::-webkit-scrollbar{display:none}:host .pages ::ng-deep [kbGalleryPage]{flex-basis:100%;flex-grow:0;flex-shrink:0;scroll-snap-align:start;scroll-snap-stop:always}\n"] }]
        }], ctorParameters: () => [], propDecorators: { automaticNavigation: [{
                type: Input
            }], pagesElement: [{
                type: ViewChild,
                args: ['pagesElement', { static: true }]
            }], enableHover: [{
                type: HostListener,
                args: ['mouseenter']
            }], disableHover: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { GalleryComponent, GalleryIntl, GalleryPageDirective, provideGalleryIntl };
